// src/components/ManageListsModal.js
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from '../context/UserProvider';
import { useTheme } from '../context/ThemeContext';
import { createList, renameList, deleteList, mergeListsToNew } from '../services/listManager';
import ConfirmModal from './ConfirmModal';

// Spinner component for loading states
const Spinner = ({ size = 16, color = 'currentColor' }) => (
  <svg
    className="animate-spin"
    style={{ width: size, height: size }}
    viewBox="0 0 24 24"
    fill="none"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke={color}
      strokeWidth="3"
      fill="none"
    />
    <path
      className="opacity-75"
      fill={color}
      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
    />
  </svg>
);

// Icon components
const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="16" />
    <line x1="8" y1="12" x2="16" y2="12" />
  </svg>
);

const EditIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const TrashIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ListIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" />
    <line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
);

const MergeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 6l4-4 4 4" />
    <path d="M12 2v10.3a4 4 0 0 1-1.172 2.872L4 22" />
    <path d="m20 22-5-5" />
  </svg>
);

// Error message component
const ErrorMessage = ({ message }) => {
  if (!message) return null;
  return (
    <p className="text-xs mt-2 flex items-center gap-1" style={{ color: '#ef4444' }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      {message}
    </p>
  );
};

// Success message component
const SuccessMessage = ({ message }) => {
  if (!message) return null;
  return (
    <p className="text-xs mt-2 flex items-center gap-1" style={{ color: '#22c55e' }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
      {message}
    </p>
  );
};

export default function ManageListsModal({ open, onClose }) {
  const { uid, settings } = useUser();
  const { theme: th, isForest } = useTheme();
  const { t } = useTranslation();
  const listsObj = React.useMemo(() => settings?.lists || {}, [settings?.lists]);
  const listNames = React.useMemo(() => {
    const names = Object.keys(listsObj).filter((n) => n !== 'default');
    names.sort((a, b) => a.localeCompare(b));
    return ['default', ...names];
  }, [listsObj]);

  // Create state
  const [creating, setCreating] = React.useState(false);
  const [newName, setNewName] = React.useState('');
  const [createError, setCreateError] = React.useState('');
  const [createSuccess, setCreateSuccess] = React.useState('');

  // Rename state
  const [renaming, setRenaming] = React.useState(false);
  const [from, setFrom] = React.useState('default');
  const [to, setTo] = React.useState('');
  const [renameError, setRenameError] = React.useState('');
  const [renameSuccess, setRenameSuccess] = React.useState('');

  // Delete state
  const [deleting, setDeleting] = React.useState(false);
  const [delName, setDelName] = React.useState('default');

  // Merge state - now supports multi-select
  const [merging, setMerging] = React.useState(false);
  const [selectedMergeLists, setSelectedMergeLists] = React.useState([]);
  const [mergedName, setMergedName] = React.useState('');
  const [mergeError, setMergeError] = React.useState('');
  const [mergeSuccess, setMergeSuccess] = React.useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);

  // Reset errors when inputs change
  React.useEffect(() => { setCreateError(''); setCreateSuccess(''); }, [newName]);
  React.useEffect(() => { setRenameError(''); setRenameSuccess(''); }, [from, to]);
  React.useEffect(() => { setMergeError(''); setMergeSuccess(''); }, [selectedMergeLists, mergedName]);

  if (!open) return null;
  if (!uid) return null;

  const overlayStyle = {
    backgroundColor: isForest ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(4px)',
  };

  const cardStyle = {
    backgroundColor: isForest ? th.surface.card : th.surface.card,
    border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : th.surface.cardBorder}`,
    boxShadow: isForest
      ? '0 25px 50px -12px rgba(0,0,0,0.5)'
      : '0 25px 50px -12px rgba(16, 185, 129, 0.25)',
  };

  // naim-web uses green gradient
  const headerGradient = isForest
    ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
    : 'linear-gradient(135deg, #10b981 0%, #059669 100%)';

  const inputStyle = {
    backgroundColor: isForest ? 'rgba(5, 46, 35, 0.8)' : th.input.background,
    border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : th.input.border}`,
    color: isForest ? th.text.primary : th.text.primary,
  };

  const selectStyle = {
    ...inputStyle,
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='${isForest ? '%2394a3b8' : '%234A6B85'}' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    paddingRight: '36px',
    appearance: 'none',
  };

  const sectionStyle = {
    backgroundColor: isForest ? 'rgba(5, 46, 35, 0.6)' : 'rgba(248, 250, 252, 0.8)',
    border: `1px solid ${isForest ? 'rgba(255,255,255,0.08)' : 'rgba(203, 213, 225, 0.5)'}`,
    borderRadius: '12px',
    padding: '16px',
  };

  // Handle multi-select toggle for merge lists
  const handleMergeListToggle = (listName) => {
    setSelectedMergeLists(prev => {
      if (prev.includes(listName)) {
        return prev.filter(n => n !== listName);
      }
      return [...prev, listName];
    });
  };

  const doCreate = async () => {
    if (!newName.trim()) {
      setCreateError(t('manageLists.enterListName'));
      return;
    }
    // Check if name already exists
    if (listNames.includes(newName.trim())) {
      setCreateError(t('manageLists.listAlreadyExists'));
      return;
    }
    try {
      setCreating(true);
      setCreateError('');
      const result = await createList(uid, newName.trim());
      if (result.created) {
        setNewName('');
        setCreateSuccess(t('manageLists.listCreated', { name: result.name }));
      } else if (result.reason === 'exists') {
        setCreateError(t('manageLists.listAlreadyExists'));
      }
    } catch (e) {
      console.error('[ManageListsModal] create failed', e);
      const errorMsg = String(e.message || '').toLowerCase();
      if (errorMsg.includes('exists')) {
        setCreateError(t('manageLists.listAlreadyExists'));
      } else {
        setCreateError(t('manageLists.failedToCreate'));
      }
    } finally {
      setCreating(false);
    }
  };

  const doRename = async () => {
    if (!from || !to.trim()) {
      setRenameError(t('manageLists.selectListAndEnterName'));
      return;
    }
    if (from === to.trim()) {
      setRenameError(t('manageLists.newNameMustBeDifferent'));
      return;
    }
    // Check if destination name already exists
    if (listNames.includes(to.trim())) {
      setRenameError(t('manageLists.listAlreadyExists'));
      return;
    }
    try {
      setRenaming(true);
      setRenameError('');
      const result = await renameList(uid, from, to.trim());
      if (result.renamed) {
        setFrom('default');
        setTo('');
        setRenameSuccess(t('manageLists.listRenamed', { from: result.from, to: result.to }));
      }
    } catch (e) {
      console.error('[ManageListsModal] rename failed', e);
      const errorMsg = String(e.message || '').toLowerCase();
      if (errorMsg.includes('exists') || errorMsg.includes('destination')) {
        setRenameError(t('manageLists.listAlreadyExists'));
      } else {
        setRenameError(t('manageLists.failedToRename'));
      }
    } finally {
      setRenaming(false);
    }
  };

  const doDelete = async () => {
    if (!delName || delName === 'default') return;
    setDeleteConfirmOpen(false);
    try {
      setDeleting(true);
      await deleteList(uid, delName);
      setDelName('default');
    } catch (e) {
      console.error('[ManageListsModal] delete failed', e);
    } finally {
      setDeleting(false);
    }
  };

  const doMerge = async () => {
    if (selectedMergeLists.length < 2) {
      setMergeError(t('manageLists.selectAtLeast2Lists'));
      return;
    }
    if (!mergedName.trim()) {
      setMergeError(t('manageLists.enterMergedListName'));
      return;
    }
    // Check if name already exists
    if (listNames.includes(mergedName.trim())) {
      setMergeError(t('manageLists.listAlreadyExists'));
      return;
    }
    try {
      setMerging(true);
      setMergeError('');
      const result = await mergeListsToNew(uid, selectedMergeLists, mergedName.trim());
      if (result.merged) {
        setMergedName('');
        setSelectedMergeLists([]);
        setMergeSuccess(t('manageLists.mergeSuccess', { name: result.newListName, itemCount: result.itemCount, listCount: result.listsProcessed || selectedMergeLists.length }));
      } else if (result.reason === 'exists') {
        setMergeError(t('manageLists.listAlreadyExists'));
      }
    } catch (e) {
      console.error('[ManageListsModal] merge failed', e);
      const errorMsg = String(e.message || '').toLowerCase();
      if (errorMsg.includes('exists')) {
        setMergeError(t('manageLists.listAlreadyExists'));
      } else {
        setMergeError(t('manageLists.failedToMerge'));
      }
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 transition-opacity duration-300"
        style={overlayStyle}
        onClick={onClose}
      />

      {/* Modal Card */}
      <div
        className="relative w-full max-w-md max-h-[90vh] rounded-2xl overflow-hidden transform transition-all duration-300"
        style={cardStyle}
      >
        {/* Header */}
        <div
          className="px-6 py-4 flex items-center justify-between"
          style={{ background: headerGradient }}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-white/20">
              <ListIcon />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">{t('manageLists.title')}</h2>
              <p className="text-xs text-white/70">{t('converters.listCount', { count: listNames.length })}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-white"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4 overflow-y-auto max-h-[calc(90vh-80px)]">
          {/* Create Section */}
          <section style={sectionStyle}>
            <div className="flex items-center gap-2 mb-3">
              <span style={{ color: th.interactive.success }}><PlusIcon /></span>
              <h3 className="text-sm font-medium" style={{ color: isForest ? th.text.primary : th.text.primary }}>
                {t('manageLists.createNewList')}
              </h3>
            </div>
            <div className="flex gap-2">
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && doCreate()}
                placeholder={t('manageLists.newListPlaceholder')}
                className="flex-1 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 transition-all"
                style={{
                  ...inputStyle,
                  '--tw-ring-color': th.interactive.primary,
                }}
              />
              <button
                onClick={doCreate}
                disabled={creating || !newName.trim()}
                className="px-4 py-2.5 text-sm font-medium rounded-lg text-white transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                style={{ backgroundColor: th.interactive.success }}
              >
                {creating ? <Spinner size={16} color="white" /> : t('manageLists.create')}
              </button>
            </div>
            <ErrorMessage message={createError} />
            <SuccessMessage message={createSuccess} />
          </section>

          {/* Rename Section */}
          <section style={sectionStyle}>
            <div className="flex items-center gap-2 mb-3">
              <span style={{ color: th.interactive.primary }}><EditIcon /></span>
              <h3 className="text-sm font-medium" style={{ color: isForest ? th.text.primary : th.text.primary }}>
                {t('manageLists.renameList')}
              </h3>
            </div>
            <div className="flex gap-2 flex-wrap sm:flex-nowrap">
              <select
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 transition-all min-w-[120px]"
                style={selectStyle}
              >
                {listNames.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              <span
                className="hidden sm:flex items-center px-2 text-sm"
                style={{ color: th.text.tertiary }}
              >
                →
              </span>
              <input
                value={to}
                onChange={(e) => setTo(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && from !== 'default' && doRename()}
                placeholder={t('manageLists.enterNewName')}
                className="flex-1 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 transition-all min-w-[100px]"
                style={inputStyle}
              />
              <button
                onClick={doRename}
                disabled={renaming || from === 'default' || !to.trim()}
                className="px-4 py-2.5 text-sm font-medium rounded-lg text-white transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                style={{ backgroundColor: th.interactive.primary }}
              >
                {renaming ? <Spinner size={16} color="white" /> : t('manageLists.rename')}
              </button>
            </div>
            {from === 'default' && (
              <p className="text-xs mt-2 flex items-center gap-1" style={{ color: th.text.tertiary }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                {t('manageLists.defaultCannotBeRenamed')}
              </p>
            )}
            <ErrorMessage message={renameError} />
            <SuccessMessage message={renameSuccess} />
          </section>

          {/* Merge Section - Multi-select */}
          <section style={sectionStyle}>
            <div className="flex items-center gap-2 mb-3">
              <span style={{ color: '#8b5cf6' }}><MergeIcon /></span>
              <h3 className="text-sm font-medium" style={{ color: isForest ? th.text.primary : th.text.primary }}>
                {t('manageLists.mergeLists')}
              </h3>
            </div>
            <p className="text-xs mb-3" style={{ color: th.text.secondary }}>
              {t('manageLists.mergeDescription')}
            </p>

            {/* Multi-select checkboxes */}
            <div className="mb-3 max-h-32 overflow-y-auto rounded-lg p-2" style={{
              backgroundColor: isForest ? 'rgba(5, 46, 35, 0.7)' : 'rgba(241, 245, 249, 0.8)',
              border: `1px solid ${isForest ? 'rgba(255,255,255,0.08)' : 'rgba(203, 213, 225, 0.5)'}`,
            }}>
              {listNames.map(name => (
                <label
                  key={name}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer hover:bg-white/5 transition-colors"
                  style={{ color: isForest ? '#e8f5e9' : th.text.primary }}
                >
                  <input
                    type="checkbox"
                    checked={selectedMergeLists.includes(name)}
                    onChange={() => handleMergeListToggle(name)}
                    className="w-4 h-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                  />
                  <span className="text-sm">{name}</span>
                </label>
              ))}
            </div>

            {/* Selected lists display */}
            {selectedMergeLists.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {selectedMergeLists.map(name => (
                  <span
                    key={name}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs"
                    style={{
                      backgroundColor: isForest ? 'rgba(139, 92, 246, 0.2)' : 'rgba(139, 92, 246, 0.1)',
                      color: isForest ? '#c4b5fd' : '#8b5cf6',
                      border: `1px solid ${isForest ? 'rgba(139, 92, 246, 0.4)' : 'rgba(139, 92, 246, 0.3)'}`,
                    }}
                  >
                    {name}
                    <button
                      onClick={() => handleMergeListToggle(name)}
                      className="hover:opacity-70"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <input
                value={mergedName}
                onChange={(e) => setMergedName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && selectedMergeLists.length >= 2 && doMerge()}
                placeholder={t('manageLists.mergedListName')}
                className="flex-1 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 transition-all"
                style={{
                  ...inputStyle,
                  '--tw-ring-color': '#8b5cf6',
                }}
              />
              <button
                onClick={doMerge}
                disabled={merging || selectedMergeLists.length < 2 || !mergedName.trim()}
                className="px-4 py-2.5 text-sm font-medium rounded-lg text-white transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                style={{ backgroundColor: '#8b5cf6' }}
              >
                {merging ? <Spinner size={16} color="white" /> : t('manageLists.merge')}
              </button>
            </div>
            {selectedMergeLists.length > 0 && selectedMergeLists.length < 2 && (
              <p className="text-xs mt-2 flex items-center gap-1" style={{ color: th.text.tertiary }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                {t('manageLists.selectAtLeast2ListsHint')}
              </p>
            )}
            <ErrorMessage message={mergeError} />
            <SuccessMessage message={mergeSuccess} />
          </section>

          {/* Delete Section */}
          <section style={sectionStyle}>
            <div className="flex items-center gap-2 mb-3">
              <span style={{ color: th.interactive.error }}><TrashIcon /></span>
              <h3 className="text-sm font-medium" style={{ color: isForest ? th.text.primary : th.text.primary }}>
                {t('manageLists.deleteList')}
              </h3>
            </div>
            <div className="flex gap-2">
              <select
                value={delName}
                onChange={(e) => setDelName(e.target.value)}
                className="flex-1 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 transition-all"
                style={selectStyle}
              >
                {listNames.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              <button
                onClick={() => setDeleteConfirmOpen(true)}
                disabled={deleting || delName === 'default'}
                className="px-4 py-2.5 text-sm font-medium rounded-lg text-white transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                style={{ backgroundColor: th.interactive.error }}
              >
                {deleting ? <Spinner size={16} color="white" /> : t('common.delete')}
              </button>
            </div>
            {delName === 'default' && (
              <p className="text-xs mt-2 flex items-center gap-1" style={{ color: th.text.tertiary }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                {t('manageLists.defaultCannotBeDeleted')}
              </p>
            )}
          </section>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={doDelete}
        title={t('manageLists.deleteList')}
        message={t('manageLists.confirmDeleteMessage', { name: delName })}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        destructive={true}
      />
    </div>
  );
}
